import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import KnakLandingHelmet from 'common/helmets/KnakLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import Felix from 'img/landing/felix.webp';
import KnakWordmark from 'img/landing/knak-wordmark.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const KnakLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Knak Case Study');
  }, [trackEvent]);

  return (
    <div className="landingKnak landingCustomersPage">
      <KnakLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Knak's customer success team uses Canny to streamline feedback processes" />
          <div className="headerImage">
            <div className="nametag">
              <div className="name">Felix Higgs</div>
              <div className="title">Senior CSM at Knak</div>
            </div>
            <LazyLoadedImage loading="eager" src={Felix} alt="Felix Higgs" />
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              Some projects shouldn’t take days or weeks to get out the door. Knak is designed to
              easily create emails and landing pages that integrate seamlessly into your marketing
              automation&nbsp;platform.
            </p>
            <p>
              Felix Higgs is Senior Customer Success Manager at Knak. He runs the Customer Success
              team internally, as well as assisting some of Knak's larger&nbsp;clients.
            </p>
            <p>
              He keeps customers happy, runs onboarding, does regular check-ins, and provides
              guidance and best practices around email and landing page&nbsp;creation.
            </p>
            <p>
              He also works closely with all other departments, from development to marketing, to
              feed information from&nbsp;customers.
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>The success team at Knak was facing two main&nbsp;problems:</p>
            <p>First, they didn’t have a single source of truth for user&nbsp;feedback.</p>
            <p>
              Different teams were using their own methods for collecting feedback. So, it was
              scattered across various channels, from Google Sheets to&nbsp;Salesforce.
            </p>
            <p className="quote">
              &ldquo;It was impossible to get a good view of all the feedback, and correlate
              the&nbsp;data.&rdquo;
            </p>
            <p>
              The second problem was that there was no way for customers to submit their
              own&nbsp;requests.
            </p>
            <p>
              The feedback had to come through the Customer Success team, who would then pass
              feedback along. This solution was inefficient, time consuming, and just
              didn’t&nbsp;work.
            </p>
            <p className="quote">
              &ldquo;We said, ‘There has to be a way to do this, there’s tools for everything.’ So,
              we started&nbsp;looking.&rdquo;
            </p>
            <p>
              The team began testing different tools. But, the feedback management tools they tried
              were always part of a bigger tool with a lot of features they didn’t want
              or&nbsp;need.
            </p>
            <p>
              Canny, however, seemed to be focused on exactly what they had in mind, without the
              extra unnecessary bells and&nbsp;whistles.
            </p>
            <p className="quote">
              &ldquo;We didn’t see anyone else doing exactly what we wanted quite as well as Canny
              does&nbsp;it.&rdquo;
            </p>
            <p>So, they decided to give Canny a&nbsp;try.</p>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              Felix, who was in charge of researching tools and getting Canny up and running, found
              the whole process incredibly&nbsp;easy.
            </p>
            <p className="quote">
              &ldquo;It was very smooth and easy. Getting the whole platform set up was
              really&nbsp;quick.&rdquo;
            </p>
            <p>
              He was also impressed with the integrations. From Intercom to SalesForce, everything
              they needed was available and easy to&nbsp;connect.
            </p>
            <p>
              Canny is now primarily managed by the Customer Success team. Within Canny, they
              regularly discuss feedback, respond to customers, update statuses, and so&nbsp;on.
            </p>
            <p>
              However, nearly everyone in the company looks at their Canny board on a
              regular&nbsp;basis.
            </p>
            <p>
              Knak’s C-level team loves it, and the development team also likes to see what
              customers are&nbsp;requesting.
            </p>
            <p className="quote">
              &ldquo;We use a lot of tech here, and of all the things we’ve brought on, nobody’s had
              anything bad to say. Everyone’s a&nbsp;fan.&rdquo;
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              The main benefit of using Canny was finally having a centralized view of all
              customer&nbsp;feedback.
            </p>
            <p className="quote">
              &ldquo;Gathering and analyzing feedback is so much more quick and efficient now, and
              it’s saving us a lot of&nbsp;time.&rdquo;
            </p>
            <p>
              Knak also actively uses the Changelog, as they release a lot of features. With Canny’s
              Changelog, they can now easily communicate updates to&nbsp;customers.
            </p>
            <p className="quote">
              &ldquo;Our users aren’t in the dark about what has been done and what hasn’t—they’re
              immediately notified whenever we ship&nbsp;something.&rdquo;
            </p>
            <p>
              Internally, Felix is a fan of the filtering feature, which allows teams to get more
              insights, and helps a lot with&nbsp;prioritization.
            </p>
            <p className="quote">
              &ldquo;It just confirms whether you’re working on the right&nbsp;things.&rdquo;
            </p>
            <p>
              When it comes to whether a specialized feedback tool is needed or not,
              Felix&nbsp;says:
            </p>
            <p className="quote">
              &ldquo;I would say do it. It gives you a holistic view of feedback, which is very hard
              to have without a tool like this. For the cost, it’s an easy&nbsp;win.&rdquo;
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage alt="Knak Wordmark" className="knakWordmark" src={KnakWordmark} />
            Knak is a codeless email and landing page creation platform for enterprise
            marketing&nbsp;teams.
            <a href="https://knak.com/" rel="noopener" target="_blank">
              knak.com
            </a>
            <div className="stat">
              <UppercaseHeader>Location</UppercaseHeader>
              <p>Ottawa, Canada</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2020</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>1,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default hot(KnakLanding);
